import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import apiInstance from '../../utils/axios';
import GetCurrentAddress from '../plugin/UserCountry';
import UserData from '../plugin/UserData';
import getCartID from '../plugin/cartID';
import Swal from 'sweetalert2';
import { CartContext } from '../../App';
import PriceFormat_Basic from '../../components/commerce-ui/price-format-basic';

const Toast = Swal.mixin({
  toast: true,
  position: 'top',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
});

function Search() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('query');

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeFilters, setActiveFilters] = useState({
    categories: [],
    vendors: [],
    priceRange: { min: 0, max: 1000 },
  });
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('relevant');
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [cartCount, setCartCount] = useContext(CartContext);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle responsive breakpoints
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const currentAddress = GetCurrentAddress();
  const userData = UserData();
  const cart_id = getCartID();

  // Fetch products based on search query
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch categories for filters
        const categoriesRes = await apiInstance.get('category/');
        setCategories(categoriesRes.data);
        
        // Fetch products based on search query
        if (query) {
          const productsRes = await apiInstance.get(`search/?query=${encodeURIComponent(query)}`);
          setProducts(productsRes.data);
        } else {
          const productsRes = await apiInstance.get('products/');
          setProducts(productsRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        Toast.fire({
          icon: 'error',
          title: 'Error al buscar',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [query]);

  // Apply filters and sorting
  useEffect(() => {
    if (!loading && products.length > 0) {
      let results = [...products];

      // Apply category filters
      if (activeFilters.categories.length > 0) {
        results = results.filter(product => 
          activeFilters.categories.includes(product.category?.title)
        );
      }

      // Apply vendor filters
      if (activeFilters.vendors.length > 0) {
        results = results.filter(product => 
          activeFilters.vendors.includes(product.vendor?.name)
        );
      }

      // Apply price range filter
      results = results.filter(product => {
        const price = parseFloat(product.price);
        return price >= activeFilters.priceRange.min && price <= activeFilters.priceRange.max;
      });

      // Apply sorting
      switch (sortBy) {
        case 'price-low':
          results.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
          break;
        case 'price-high':
          results.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
          break;
        case 'newest':
          results.sort((a, b) => new Date(b.date) - new Date(a.date));
          break;
        default:
          break;
      }

      setFilteredProducts(results);
    }
  }, [products, loading, activeFilters, sortBy]);

  const handleFilterChange = (filterType, value) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter(item => item !== value)
        : [...prev[filterType], value]
    }));
  };

  const handlePriceRangeChange = (min, max) => {
    setActiveFilters(prev => ({
      ...prev,
      priceRange: { min, max }
    }));
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    if (category) {
      setActiveFilters(prev => ({
        ...prev,
        categories: [category.title]
      }));
    } else {
      setActiveFilters(prev => ({
        ...prev,
        categories: []
      }));
    }
  };

  const clearAllFilters = () => {
    setActiveFilters({
      categories: [],
      vendors: [],
      priceRange: { min: 0, max: 1000 },
    });
    setSelectedCategory(null);
  };

  // Add to cart function
  const addToCart = async (productId, price, shipping_ammount) => {
    const formdata = new FormData();
    formdata.append('product_id', productId);
    formdata.append('user_id', userData?.user_id || 0);
    formdata.append('qty', 1);
    formdata.append('price', price);
    formdata.append('shipping_ammount', shipping_ammount);
    formdata.append('country', currentAddress.country);
    formdata.append('size', 'No Size');
    formdata.append('color', 'No Color');
    formdata.append('cart_id', cart_id);

    try {
      const response = await apiInstance.post('cart-view/', formdata);
      Toast.fire({
        icon: 'success',
        title: response.data.message,
      });
      
      const cartResponse = await apiInstance.get(`cart-list/${cart_id}/`);
      setCartCount(cartResponse.data.length);
    } catch (error) {
      console.error('Error adding to cart:', error);
      let errorMessage = 'Error al agregar al carrito';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      Toast.fire({
        icon: 'error',
        title: errorMessage,
      });
    }
  };

  const uniqueVendors = [...new Set(filteredProducts.map(p => p.vendor?.name))].filter(Boolean);

  return (
    <div style={{ backgroundColor: '#fafafa', minHeight: '100vh' }}>
      {/* Header Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        padding: isMobile ? '16px 12px' : '32px 20px',
        marginBottom: isMobile ? '12px' : '24px'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h1 style={{ 
            fontSize: isMobile ? '18px' : '28px', 
            fontWeight: '700', 
            color: 'white',
            marginBottom: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>🔍</span>
            {query ? `"${query}"` : 'Buscar Productos'}
          </h1>
          <p style={{ color: '#94a3b8', fontSize: isMobile ? '12px' : '14px' }}>
            {filteredProducts.length} productos encontrados
          </p>
        </div>
      </div>

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '0 10px 20px' : '0 20px 40px' }}>
        <div style={{ display: 'flex', gap: '24px' }}>
          
          {/* Left Sidebar */}
          <aside style={{
            width: '260px',
            flexShrink: 0,
            display: 'none',
          }} className="search-sidebar">
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              position: 'sticky',
              top: '20px',
              border: '1px solid #e2e8f0'
            }}>
              <h2 style={{ 
                fontSize: '16px', 
                fontWeight: '600', 
                color: '#1e293b',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '1px solid #e2e8f0'
              }}>
                Categorías
              </h2>
              
              {/* All Products Option */}
              <div
                style={{
                  padding: '10px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  marginBottom: '4px',
                  backgroundColor: !selectedCategory ? '#f1f5f9' : 'transparent',
                  color: !selectedCategory ? '#1e293b' : '#475569',
                  fontWeight: !selectedCategory ? '600' : '400',
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                }}
                onClick={() => handleCategorySelect(null)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Todos</span>
                  <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                    {filteredProducts.length}
                  </span>
                </div>
              </div>

              {/* Category List */}
              {categories.map((category) => {
                const categoryProductCount = filteredProducts.filter(p => p.category?.id === category.id).length;
                const isSelected = selectedCategory?.id === category.id;
                
                if (categoryProductCount === 0) return null;
                
                return (
                  <div
                    key={category.id}
                    style={{
                      padding: '10px 12px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      marginBottom: '4px',
                      backgroundColor: isSelected ? '#f1f5f9' : 'transparent',
                      color: isSelected ? '#1e293b' : '#475569',
                      fontWeight: isSelected ? '600' : '400',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                    }}
                    onClick={() => handleCategorySelect(category)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{category.title_translated || category.title}</span>
                      <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                        {categoryProductCount}
                      </span>
                    </div>
                  </div>
                );
              })}

              {/* Price Range */}
              <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>
                  Precio
                </h3>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="number"
                    placeholder="Mín"
                    value={activeFilters.priceRange.min}
                    onChange={(e) => handlePriceRangeChange(parseInt(e.target.value) || 0, activeFilters.priceRange.max)}
                    style={{
                      width: '80px',
                      padding: '8px 10px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '13px',
                      backgroundColor: '#f8fafc'
                    }}
                  />
                  <span style={{ color: '#94a3b8' }}>—</span>
                  <input
                    type="number"
                    placeholder="Máx"
                    value={activeFilters.priceRange.max}
                    onChange={(e) => handlePriceRangeChange(activeFilters.priceRange.min, parseInt(e.target.value) || 1000)}
                    style={{
                      width: '80px',
                      padding: '8px 10px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '13px',
                      backgroundColor: '#f8fafc'
                    }}
                  />
                </div>
              </div>

              {/* Clear Filters */}
              {(activeFilters.categories.length > 0 || activeFilters.vendors.length > 0 || 
                activeFilters.priceRange.min > 0 || activeFilters.priceRange.max < 1000) && (
                <button 
                  onClick={clearAllFilters}
                  style={{
                    width: '100%',
                    marginTop: '20px',
                    padding: '10px',
                    backgroundColor: '#475569',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  Limpiar Filtros
                </button>
              )}
            </div>
          </aside>

          {/* Main Content */}
          <main style={{ flex: 1 }}>
            {/* Sort Bar */}
            <div style={{
              backgroundColor: 'white',
              padding: '16px 20px',
              borderRadius: '10px',
              marginBottom: '20px',
              border: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <span style={{ fontSize: '14px', color: '#64748b' }}>
                {selectedCategory && (
                  <span style={{
                    backgroundColor: '#f1f5f9',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    marginRight: '8px',
                    color: '#1e293b',
                    fontWeight: '500'
                  }}>
                    {selectedCategory.title_translated || selectedCategory.title}
                  </span>
                )}
                {filteredProducts.length} resultados
              </span>
              <select 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: '13px',
                  backgroundColor: '#f8fafc',
                  cursor: 'pointer',
                  color: '#1e293b'
                }}
              >
                <option value="relevant">Más Relevante</option>
                <option value="price-low">Precio: Menor a Mayor</option>
                <option value="price-high">Precio: Mayor a Menor</option>
                <option value="newest">Más Reciente</option>
              </select>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className="search-grid">
                {[...Array(8)].map((_, i) => (
                  <div 
                    key={i} 
                    style={{
                      backgroundColor: '#e2e8f0',
                      borderRadius: '10px',
                      height: '340px',
                      animation: 'pulse 2s infinite'
                    }}
                  />
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div style={{
                backgroundColor: 'white',
                padding: '60px 20px',
                borderRadius: '12px',
                textAlign: 'center',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
                  {query ? `No se encontraron resultados para "${query}"` : 'No se encontraron productos'}
                </h3>
                <p style={{ color: '#64748b', marginBottom: '24px', fontSize: '14px' }}>
                  Intenta ajustar los filtros o buscar otro término
                </p>
                <button 
                  onClick={clearAllFilters}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#1e293b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Limpiar Filtros
                </button>
              </div>
            ) : (
              <div className="search-grid">
                {filteredProducts.map((product) => (
                  <div 
                    key={product.id}
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      border: '1px solid #e2e8f0',
                      transition: 'all 0.2s ease',
                    }}
                    className="search-product-card"
                  >
                    <Link to={`/detail/${product.slug}/`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <div style={{ position: 'relative', backgroundColor: '#f8fafc' }}>
                        <img
                          src={product.image}
                          alt={product.title_translated || product.title}
                          style={{
                            width: '100%',
                            height: '180px',
                            objectFit: 'contain',
                            padding: '12px'
                          }}
                        />
                        {product.old_price > product.price && (
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            left: '8px',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600'
                          }}>
                            -{Math.round(((product.old_price - product.price) / product.old_price) * 100)}%
                          </div>
                        )}
                      </div>
                    </Link>
                    
                    <div style={{ padding: '16px' }}>
                      <Link to={`/detail/${product.slug}/`} style={{ textDecoration: 'none', color: 'inherit' }}>
                        <h3 style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#1e293b',
                          marginBottom: '6px',
                          lineHeight: '1.4',
                          height: '40px',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {product.title_translated || product.title}
                        </h3>
                      </Link>
                      
                      <div style={{ marginBottom: '10px' }}>
                        <span style={{ fontSize: '11px', color: '#64748b' }}>
                          {product.category?.title_translated || product.category?.title}
                        </span>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
                        <span style={{ fontSize: '18px', fontWeight: '700', color: '#059669' }}>
                          <PriceFormat_Basic value={product.price} />
                        </span>
                        {product.old_price > product.price && (
                          <span style={{ fontSize: '13px', color: '#94a3b8', textDecoration: 'line-through' }}>
                            <PriceFormat_Basic value={product.old_price} />
                          </span>
                        )}
                      </div>
                      
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          addToCart(product.id, product.price, product.shipping_ammount);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px',
                          backgroundColor: '#1e293b',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease'
                        }}
                        className="add-cart-btn"
                      >
                        Agregar al Carrito
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        .search-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 16px;
        }
        
        .search-product-card:hover {
          border-color: #cbd5e1;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          transform: translateY(-2px);
        }
        
        .add-cart-btn:hover {
          background-color: #334155 !important;
        }
        
        @media (min-width: 768px) {
          .search-sidebar {
            display: block !important;
          }
        }
        
        @media (max-width: 767px) {
          .search-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }
          
          .search-grid .search-product-card img {
            height: 120px !important;
            padding: 8px !important;
          }
          
          .search-grid .search-product-card h3 {
            font-size: 12px !important;
            height: 32px !important;
            margin-bottom: 4px !important;
          }
          
          .search-grid .search-product-card > div:last-child {
            padding: 10px !important;
          }
          
          .search-grid .search-product-card button {
            padding: 8px !important;
            font-size: 12px !important;
          }
        }
        
        @media (max-width: 480px) {
          .search-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
          }
          
          .search-grid .search-product-card img {
            height: 100px !important;
          }
          
          .search-grid .search-product-card h3 {
            font-size: 11px !important;
            height: 28px !important;
          }
          
          .search-grid .search-product-card span[style*="font-size: 18px"] {
            font-size: 14px !important;
          }
        }
      `}</style>
    </div>
  );
}

export default Search;

